import React, { createContext, useState, useContext, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  // Role-based dashboard routing
  const getDefaultRoute = (userRole) => {
    switch (userRole) {
      case 'admin':
        return '/admin/dashboard';
      case 'manager':
        return '/manager/dashboard';
      case 'vendor':
        return '/vendor/dashboard';
      case 'panelist':
        return '/panelist/dashboard';
      default:
        return '/';
    }
  };

  // Check authentication status
  const checkAuthStatus = async () => {
    try {
      console.log('🔍 Checking auth status...');
      
      const response = await api.get('/auth/session', { 
        withCredentials: true, 
        headers: { 'Cache-Control': 'no-cache' }
      });
      
      console.log('🔍 Session check response:', response.data);
      
      if (response.data.authenticated && response.data.user) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        console.log('✅ User authenticated:', response.data.user);
      } else {
        console.log('❌ User is not authenticated');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.error('❌ Auth check failed:', error);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  };

  // FIXED Login function with proper error handling
  const login = async (username, password, remember = true) => {
    try {
      setLoading(true);
      
      // Ensure we're sending clean, flat data
      const loginData = {
        username: String(username).trim(),
        password: String(password).trim(),
        remember: !!remember
      };
      
      console.log('🔐 Attempting login with:', { 
        username: loginData.username, 
        passwordProvided: !!loginData.password 
      });

      // Make the login request
      const response = await api.post('/auth/login', loginData, {
        headers: {
          'Content-Type': 'application/json'
        },
        withCredentials: true
      });

      console.log('🔐 Login response:', response.data);

      if (response.data.status === 'success' && response.data.user) {
        console.log('✅ Login successful, user set:', response.data.user);
        
        setUser(response.data.user);
        setIsAuthenticated(true);

        // Role-based redirection
        const userRole = response.data.user.role;
        const defaultRoute = getDefaultRoute(userRole);
        
        console.log(`🔄 Redirecting ${userRole} to ${defaultRoute}`);

        // Use setTimeout to ensure state updates are processed
        setTimeout(() => {
          navigate(defaultRoute);
        }, 100);

        return { success: true, user: response.data.user };
      } else {
        const errorMsg = response.data.error || response.data.message || 'Login failed';
        console.error('❌ Login failed:', errorMsg);
        return { success: false, message: errorMsg };
      }
    } catch (error) {
      console.error('❌ Login error:', error);
      
      let errorMessage = 'Login failed. Please try again.';
      
      if (error.response?.status === 401) {
        errorMessage = 'Invalid username or password.';
      } else if (error.response?.status === 404) {
        errorMessage = 'Login service not available. Please check if the backend server is running.';
      } else if (error.response?.status === 500) {
        const serverError = error.response?.data?.error || 'Server error';
        errorMessage = `Server error: ${serverError}`;
      } else if (!error.response) {
        errorMessage = 'Cannot connect to server. Please check your connection.';
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }

      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Logout function
  const logout = async () => {
    try {
      console.log('🔐 Attempting logout...');
      await api.post('/auth/logout');
      console.log('✅ Logout successful');
    } catch (error) {
      console.error('❌ Logout error:', error);
    } finally {
      setUser(null);
      setIsAuthenticated(false);
      navigate('/login');
    }
  };

  // Role checking functions
  const hasRole = (requiredRole) => {
    if (!user || !isAuthenticated) return false;
    if (!requiredRole) return true;
    return user.role === requiredRole;
  };

  const isAdmin = () => hasRole('admin');
  const isManager = () => hasRole('manager');
  const isVendor = () => hasRole('vendor');
  const isPanelist = () => hasRole('panelist');

  // Update user info
  const updateUser = (updatedUserData) => {
    setUser(prevUser => ({ ...prevUser, ...updatedUserData }));
  };

  // Navigate to appropriate dashboard
  const navigateToDashboard = () => {
    if (user && isAuthenticated) {
      const defaultRoute = getDefaultRoute(user.role);
      navigate(defaultRoute);
    } else {
      navigate('/login');
    }
  };

  // Check auth status on mount
  useEffect(() => {
    checkAuthStatus();
  }, []);

  // Debug log for auth state changes
  useEffect(() => {
    console.log('🔍 AuthContext state:', {
      user: user ? { id: user.id, username: user.username, role: user.role } : null,
      isAuthenticated,
      loading
    });
  }, [user, isAuthenticated, loading]);

  const value = {
    user,
    isAuthenticated,
    loading,
    login,
    logout,
    checkAuthStatus,
    hasRole,
    isAdmin,
    isManager,
    isVendor,
    isPanelist,
    updateUser,
    navigateToDashboard,
    getDefaultRoute,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
